import { useState, useEffect } from "react";
import {
  MapPin,
  Phone,
  ExternalLink,
  Globe,
  Ban,
  Camera,
  Trash2,
  Crosshair,
  X,
  ChevronRight,
  Save,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { StatusIndicator } from "@/components/locations/StatusIndicator";
import { GoogleReviewsModal } from "@/components/review/GoogleReviewsModal";
import { ClassificationsSection } from "@/components/map/ClassificationsSection";
import { PhotoCarousel } from "@/components/map/PhotoCarousel";
import { getCourtColor } from "@/components/map/SportCourtMarker";
import { sportTypeIcons } from "@/lib/sport-icons";
import { resolveHasSportField } from "@/lib/classification";
import { trpc } from "@/lib/trpc";

interface LocationSidebarDetailProps {
  locationId: string;
  onScreenshotClick: () => void;
  onEnterSpotPlacement: () => void;
  onExitSpotPlacement: () => void;
  isSpotPlacementActive: boolean;
}

export function LocationSidebarDetail({
  locationId,
  onScreenshotClick,
  onEnterSpotPlacement,
  onExitSpotPlacement,
  isSpotPlacementActive,
}: LocationSidebarDetailProps) {
  const { data: location, isLoading } = trpc.locations.getById.useQuery(
    { id: locationId },
    { enabled: !!locationId },
  );

  const { data: screenshotData } = trpc.locations.getScreenshotUrl.useQuery(
    { locationId },
    { enabled: !!locationId && !!location?.screenshot },
  );

  const utils = trpc.useUtils();
  const spotsQuery = trpc.sportCourts.listByLocation.useQuery(
    { locationId },
    { enabled: !!locationId },
  );
  const spots = spotsQuery.data;

  const deleteCourtMutation = trpc.sportCourts.delete.useMutation({
    onSuccess: () => {
      utils.sportCourts.listByLocation.invalidate({ locationId });
      utils.sportCourts.getMapData.invalidate();
    },
  });

  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [visibilityRating, setVisibilityRating] = useState<number | null>(null);
  const [isCulturalSpot, setIsCulturalSpot] = useState(false);
  const [approvedByCity, setApprovedByCity] = useState(false);
  const [approvedBySponsor, setApprovedBySponsor] = useState(false);
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [advancedDirty, setAdvancedDirty] = useState(false);

  useEffect(() => {
    if (!location) return;
    setVisibilityRating(location.visibility_rating ?? null);
    setIsCulturalSpot(location.is_cultural_spot ?? false);
    setApprovedByCity(location.approved_by_city ?? false);
    setApprovedBySponsor(location.approved_by_sponsor ?? false);
    setOwnershipConfirmed(location.ownership_confirmed ?? false);
    setAdvancedDirty(false);
  }, [locationId, location]);

  const saveAdvancedMutation = trpc.locations.saveClassification.useMutation({
    onSuccess: () => {
      utils.locations.getById.invalidate({ id: locationId });
      setAdvancedDirty(false);
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-48 w-full rounded-lg" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (!location) return null;

  const screenshotUrl = screenshotData?.url ?? null;
  const satelliteClassification = location.classifications?.find(
    (c: { source: string }) => c.source === "satellite",
  );
  const webResearchClassification = location.classifications?.find(
    (c: { source: string }) => c.source === "web_research",
  );

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto space-y-4 p-4">
        {/* Rejected banner */}
        {location.is_rejected && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <Ban className="size-4 text-destructive shrink-0" />
            <span className="text-sm font-medium text-destructive">
              This location has been rejected
            </span>
          </div>
        )}

        {/* Screenshot */}
        <button
          type="button"
          className="relative group h-48 w-full rounded-lg overflow-hidden border bg-muted cursor-pointer"
          onClick={onScreenshotClick}
          title="Zoom to location on map"
        >
          {screenshotUrl && !imgError ? (
            <>
              {imgLoading && (
                <Skeleton className="absolute inset-0 h-full w-full rounded-lg" />
              )}
              <img
                src={screenshotUrl}
                alt={`Satellite view of ${location.name}`}
                className={`h-full w-full object-cover transition-opacity ${imgLoading ? "opacity-0" : "opacity-100"}`}
                onLoad={() => setImgLoading(false)}
                onError={() => {
                  setImgLoading(false);
                  setImgError(true);
                }}
              />
            </>
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Camera className="size-8 text-muted-foreground" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <MapPin className="size-5 text-white" />
          </div>
        </button>

        {/* Place photos */}
        {location.photo_refs?.length > 0 && (
          <PhotoCarousel photoRefs={location.photo_refs} />
        )}

        {/* Address */}
        {location.address && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Address
            </h3>
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <span>{location.address}</span>
            </div>
          </div>
        )}

        {/* Notes */}
        {location.notes && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Notes
            </h3>
            <p className="text-sm">{location.notes}</p>
          </div>
        )}

        {/* Status indicators */}
        <div className="flex gap-2">
          <StatusIndicator
            type="sport_field"
            hasSportField={resolveHasSportField(location.classifications)}
          />
          <StatusIndicator
            type="ownership"
            ownership={location.ownership}
          />
          <StatusIndicator
            type="satellite_confidence"
            confidence={satelliteClassification?.confidence ?? null}
          />
          <StatusIndicator
            type="web_confidence"
            confidence={webResearchClassification?.confidence ?? null}
          />
          <StatusIndicator
            type="reviews"
            rating={location.rating}
            reviewCount={location.user_rating_count}
            onClick={() => setShowReviewsModal(true)}
          />
        </div>

        {/* Contact info */}
        <div className="space-y-2 text-sm">
          {location.phone && (
            <div className="flex items-center gap-2">
              <Phone className="size-4 text-muted-foreground shrink-0" />
              <a
                href={`tel:${location.phone}`}
                className="text-primary hover:underline"
              >
                {location.phone}
              </a>
            </div>
          )}
        </div>

        {/* Classifications */}
        <div className="rounded-lg border bg-card p-3">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Classifications
          </h3>
          <ClassificationsSection
            classifications={location.classifications ?? []}
            ownership={location.ownership}
            hasWebsite={!!location.website}
            isRejected={!!location.is_rejected}
          />
        </div>

        {/* Sport Courts */}
        {spots && spots.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Sport Courts ({spots.length})
            </h3>
            <div className="space-y-1.5">
              {spots.map((court: any) => {
                const Icon = sportTypeIcons[court.sport_type];
                const color = getCourtColor(court.sport_type);
                return (
                  <div
                    key={court.id}
                    className="flex items-center gap-2 rounded-md border p-2 text-sm group"
                  >
                    <div
                      className="flex items-center justify-center rounded-full shrink-0"
                      style={{ width: 20, height: 20, backgroundColor: color }}
                    >
                      {Icon && <Icon size={12} stroke={1.5} color="white" />}
                    </div>
                    <span className="text-xs font-medium truncate">
                      {court.sport_type.replace(/_/g, " ")}
                    </span>
                    {court.notes && (
                      <span className="text-xs text-muted-foreground truncate flex-1">
                        {court.notes}
                      </span>
                    )}
                    <button
                      type="button"
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                      title="Delete court"
                      disabled={deleteCourtMutation.isPending}
                      onClick={() => deleteCourtMutation.mutate({ id: court.id })}
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Advanced */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <ChevronRight className={`size-4 transition-transform ${advancedOpen ? "rotate-90" : ""}`} />
            Advanced
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-3">
            {/* Rating */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Rating</Label>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {visibilityRating ?? "—"}
                </span>
              </div>
              <Slider
                min={1}
                max={5}
                step={1}
                value={visibilityRating != null ? [visibilityRating] : undefined}
                defaultValue={[3]}
                onValueChange={([v]) => {
                  setVisibilityRating(v ?? null);
                  setAdvancedDirty(true);
                }}
              />
            </div>
            {/* Boolean toggles */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="sidebar-cultural-spot" className="text-sm">Cultural Spot</Label>
                <Switch
                  id="sidebar-cultural-spot"
                  checked={isCulturalSpot}
                  onCheckedChange={(v) => { setIsCulturalSpot(v); setAdvancedDirty(true); }}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="sidebar-approved-city" className="text-sm">Approved by City</Label>
                <Switch
                  id="sidebar-approved-city"
                  checked={approvedByCity}
                  onCheckedChange={(v) => { setApprovedByCity(v); setAdvancedDirty(true); }}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="sidebar-approved-sponsor" className="text-sm">Approved by Sponsor</Label>
                <Switch
                  id="sidebar-approved-sponsor"
                  checked={approvedBySponsor}
                  onCheckedChange={(v) => { setApprovedBySponsor(v); setAdvancedDirty(true); }}
                />
              </div>
            </div>
            {/* Save button (only when dirty) */}
            {advancedDirty && (
              <Button
                size="sm"
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                disabled={saveAdvancedMutation.isPending}
                onClick={() => {
                  saveAdvancedMutation.mutate({
                    locationId,
                    userHasSportField: resolveHasSportField(location.classifications),
                    ownership: location.ownership,
                    visibilityRating,
                    isCulturalSpot,
                    approvedByCity,
                    approvedBySponsor,
                    ownershipConfirmed,
                  });
                }}
              >
                <Save className="size-4 mr-1.5" />
                {saveAdvancedMutation.isPending ? "Saving..." : "Save"}
              </Button>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Sticky bottom action bar */}
      <div className="border-t bg-background px-4 py-3 shrink-0 flex gap-2">
        {location.google_maps_url && (
          <Button
            size="lg"
            className="flex-1 gap-1.5 bg-blue-600 text-white hover:bg-blue-700"
            asChild
          >
            <a
              href={location.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="size-4" />
              Google Maps
            </a>
          </Button>
        )}
        {location.website ? (
          <Button
            size="lg"
            className="flex-1 gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
            asChild
          >
            <a
              href={location.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Globe className="size-4" />
              Website
            </a>
          </Button>
        ) : (
          <Button
            size="lg"
            className="flex-1 gap-1.5 bg-muted text-muted-foreground"
            disabled
          >
            <Globe className="size-4" />
            No website
          </Button>
        )}
        {isSpotPlacementActive ? (
          <Button
            size="lg"
            className="flex-1 gap-1.5"
            style={{ backgroundColor: "#FF646C", color: "white" }}
            onClick={onExitSpotPlacement}
          >
            <X className="size-4" />
            Exit Placement
          </Button>
        ) : (
          <Button
            size="lg"
            className="flex-1 gap-1.5"
            style={{ backgroundColor: "#f97316", color: "white" }}
            onClick={onEnterSpotPlacement}
          >
            <Crosshair className="size-4" />
            Place Courts
          </Button>
        )}
      </div>

      <GoogleReviewsModal
        open={showReviewsModal}
        onOpenChange={setShowReviewsModal}
        locationId={locationId}
      />
    </div>
  );
}
